import { db } from "../lib/firebase";
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    limit
} from "firebase/firestore";
import { normalizeProduct } from "../utils/product";

const COLLECTION = "products";

class ProductService {
    // 1. Get All Products (Admin mostly, but now robust)
    async getAllProducts() {
        try {
            console.log(`[ProductService] Fetching ALL products from collection: ${COLLECTION}`);
            // Try ordered first
            try {
                const q = query(collection(db, COLLECTION), orderBy("createdAt", "desc"));
                const snapshot = await getDocs(q);
                console.log(`[ProductService] Snapshot size: ${snapshot.size}`);
                if (snapshot.size > 0) {
                    console.log("[ProductService] First doc data:", snapshot.docs[0].data());
                }
                return snapshot.docs.map(normalizeProduct);
            } catch (idxError) {
                console.warn("[ProductService] Index missing for getAllProducts, falling back to unordered", idxError);
                const snapshot = await getDocs(collection(db, COLLECTION));
                console.log(`[ProductService] Fallback Snapshot size: ${snapshot.size}`);
                // Sort manually if index fails
                const docs = snapshot.docs.map(normalizeProduct);
                return docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            }
        } catch (error) {
            console.error("[ProductService] Error fetching all products:", error);
            throw error;
        }
    }

    // 2. Get Active Products by Category (Storefront)
    async getProductsByCategory(categorySlug) {
        try {
            const q = query(
                collection(db, COLLECTION),
                where("category", "==", categorySlug),
                where("isActive", "==", true)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(normalizeProduct);
        } catch (error) {
            console.error("Error fetching category products:", error);
            // Fallback: client-side filter if index issues (optional but safer)
            return [];
        }
    }

    // 3. Get Single Product
    async getProductById(id) {
        try {
            if (!id) return null;
            const docRef = doc(db, COLLECTION, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return normalizeProduct(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Error fetching product:", error);
            return null;
        }
    }

    // 4. Add Product (Admin)
    async addProduct(productData) {
        try {
            // Field Normalization at Write Time
            const newProduct = {
                name: productData.name || productData.title || "Untitled",
                image: productData.image || productData.imageUrl || "",
                category: productData.category || "uncategorized",
                price: Number(productData.price) || 0,
                description: productData.description || "",
                form: productData.form || "", // e.g. Tablet, Kapsül
                stock: Number(productData.stock) ?? 999,
                isActive: productData.isActive !== false, // Default true
                inStock: productData.inStock !== false, // Default true
                isBestseller: productData.isBestseller || false,
                isFeatured: productData.isFeatured || false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                // Store legacy/other fields if needed, spread remaining
                ...productData
            };

            // Clean up mapped fields to avoid duplication if desired, 
            // but keeping them doesn't hurt much.

            const docRef = await addDoc(collection(db, COLLECTION), newProduct);
            return docRef.id;
        } catch (error) {
            console.error("Error adding product:", error);
            throw error;
        }
    }

    // 5. Update Product (Admin)
    async updateProduct(id, updates) {
        try {
            const docRef = doc(db, COLLECTION, id);

            // normalize updates lightly
            const safeUpdates = {
                ...updates,
                updatedAt: serverTimestamp()
            };

            await updateDoc(docRef, safeUpdates);
            return true;
        } catch (error) {
            console.error("Error updating product:", error);
            throw error;
        }
    }

    // 6. Delete Product (Admin)
    async deleteProduct(id) {
        try {
            await deleteDoc(doc(db, COLLECTION, id));
            return true;
        } catch (error) {
            console.error("Error deleting product:", error);
            throw error;
        }
    }
    // 7. Get Recent Products (Home Fallback)
    async getRecentProducts(count = 8) {
        try {
            const q = query(
                collection(db, COLLECTION),
                where("isActive", "==", true),
                orderBy("createdAt", "desc"),
                limit(count)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(normalizeProduct);
        } catch (error) {
            console.error("Error fetching recent products, using fallback sort:", error);
            // Fallback: fetch all active and sort
            try {
                const q = query(collection(db, COLLECTION), where("isActive", "==", true));
                const snapshot = await getDocs(q);
                let all = snapshot.docs.map(normalizeProduct);
                all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                return all.slice(0, count);
            } catch (e) {
                return [];
            }
        }
    }

    // 8. Get Best Sellers (Home Fallback)
    async getBestSellers(count = 8) {
        try {
            const q = query(
                collection(db, COLLECTION),
                where("isActive", "==", true),
                where("isBestseller", "==", true),
                limit(count)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(normalizeProduct);
        } catch (error) {
            console.error("Error fetching bestsellers:", error);
            return [];
        }
    }
}

export const productService = new ProductService();
